import CardDropdown from '@/components/common/CardDropdown';
import { useState } from 'react';
import Flex from '@/components/common/Flex';
import {
  Row,
  Col,
  Button,
  Form
} from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconItem from '@/components/common/icon/IconItem';
import StatsChart from './components/StatsChart';
import { getColor } from '@/helpers/utils';
import DomainCard from './components/DomainCard';
import {
  useGetDomainsCountQuery,
  useGetDomainsQuery,
  useRemoveAccessMutation,
  useRemoveDomainMutation
} from '@/app/Domain/api/domainsApi';
import { useSearchParams } from 'react-router-dom';
import authApi, { useGetUserInfoQuery } from '@/app/common/api/authApi';
import PaginationDomains from './components/PaginationDomains';
import ManageMemebersDrawer from './components/ManageMemebersDrawer';
import CancelModal from '@/app/common/components/CancelModal';
import AddDomainDrawer from './components/AddDomainDrawer';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import DomainCardSkeleton from './components/DomainCardSkeleton';
import PaginationDomainsSkeleton from './components/PaginationDomainsSkeleton';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import NoDomainData from './components/NoDomainData';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import { useGetTariffInfoQuery } from '../KeywordTracker/api/keywordsApi';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

type StatItem = {
  id: number;
  title: string;
  amount: number;
  target: string;
  icon: IconProp;
  caret: IconProp;
  color: string;
  caretColor: string;
  data: number[];
};

type TriggeredRemState = {
  url: string;
  slug: string;
  mode: string;
};

const statsData: StatItem[] = [
  {
    id: 1,
    title: 'میانگین رتبه',
    amount: 15,
    target: '6',
    icon: 'phone',
    caret: 'caret-up',
    color: 'primary',
    caretColor: 'success',
    data: [
      220, 230, 150, 175, 200, 170, 70, 160, 220, 230, 30, 175, 200, 170, 70,
      160
    ]
  }
];

const StatsItem = ({ stat }: { stat: StatItem }) => {
  const { icon, color, title, amount, caret, caretColor, target, data } = stat;
  return (
    <>
      <Flex
        justifyContent="center"
        alignItems="center"
        className="mb-3 position-static"
      >
        <IconItem
          tag="div"
          icon={icon}
          bg={`soft-${color}`}
          color={color}
          size="sm"
          iconClass="fs--2"
          className="me-2 shadow-none"
        />
        <h6 className="mb-0 flex-1">{title}</h6>
        <div>
          <CardDropdown />
        </div>
      </Flex>
      <Flex>
        <p className="font-sans-serif lh-1 mb-1 fs-4 pe-2">{amount}%</p>
        <div className="d-flex flex-column">
          <FontAwesomeIcon
            icon={caret}
            className={`me-1 mb-0 text-${caretColor}`}
          />
          <p className="fs--2 mb-0 mt-0 text-nowrap">{target}</p>
        </div>
        <div className="w-100 ms-2">
          <StatsChart color={getColor(color)} data={data} />
        </div>
      </Flex>
    </>
  );
};

const Overview = () => {
  const { data: userInfo, isLoading } = useGetUserInfoQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [removeDomain] = useRemoveDomainMutation();
  const [removeAccess] = useRemoveAccessMutation();
  const page = Number(searchParams.get('p')) || 1;
  const [triggeredRem, setTriggeredRem] = useState<TriggeredRemState>({
    url: '',
    slug: '',
    mode: ''
  });
  const paginationState = {
    page,
    perPage: 5
  };
  const dispatch = useDispatch();
  const [stats] = useState(statsData);
  const [searchText, setSearchText] = useState('');
  const [searchTextBody, setSearchTextBody] = useState('');
  const { isLoading: isLoadingPagination, data: initDomainsData } =
    useGetDomainsCountQuery({
      page: 1,
      perPage: 5,
      search_text: searchTextBody
    });
  const { isFetching, data: domainsData } = useGetDomainsQuery(
    { page, perPage: 5, search_text: searchTextBody },
    { skip: isLoadingPagination }
  );
  const { data: limitInfo, isFetching: isFetchingLimitInfo } =
    useGetTariffInfoQuery(null);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  // useEffect(() => {
  //   setBreadcrumbData([{ text: 'نمای کلی', href: '#' }]);
  //   dispatch(setFilters({}));
  // }, []);
  const isPanelOver = () => limitInfo?.data?.tariff?.expired_panel;
  const isDomainsOver = () =>
    limitInfo?.data?.used?.num_of_domains >=
    limitInfo?.data?.tariff?.num_of_domains;
  return (
    <>
      <ToolsBreadcrumb
        parentName="دامنه ها"
        parentUrl={`/domains/`}
        childs={[]}
      />
      <CancelModal
        submitTitle="آره، حذف بشه"
        desc={
          <>
            آیا از حذف{' '}
            {triggeredRem.mode === 'delete_domain' ? 'دامنه' : 'دسترسی دامنه'}{' '}
            <strong className="fw-black">{triggeredRem.url}</strong> مطمئن هستی؟
          </>
        }
        show={triggeredRem.url}
        title={
          triggeredRem.mode === 'delete_domain' ? 'حذف دامنه' : 'حذف دسترسی'
        }
        setShow={setTriggeredRem}
        submitFunc={() => {
          triggeredRem.mode === 'delete_domain'
            ? removeDomain(triggeredRem.slug)
                .unwrap()
                .then(() => {
                  successMessage('دامنه با موفقیت حذف شد');
                  dispatch(authApi.util.invalidateTags(['UserInfo']));
                })
                .catch(err => {
                  errorMessage('خطایی در حذف دامنه وجود داره');
                  console.error(err);
                })
            : removeAccess(triggeredRem.slug)
                .unwrap()
                .then(() => {
                  successMessage('دسترسی با موفقیت حذف شد');
                })
                .catch(err => {
                  errorMessage('خطایی در حذف دسترسی وجود داره');
                  console.error(err);
                });
        }}
      />
      <AddDomainDrawer
        maxCompetitors={limitInfo?.data?.tariff?.num_of_competitors}
        show={showAddDrawer}
        setShow={setShowAddDrawer}
      />
      <ManageMemebersDrawer
        closeHandler={() => {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('domain_slug');
          newSearchParams.delete('url');
          setSearchParams(newSearchParams);
        }}
        domain_slug={searchParams.get('domain_slug')}
        show={searchParams.has('domain_slug')}
      />
      <Row className="g-0 my-3 p-0 justify-content-between">
        <Col sm="auto">
          <ChildrensTooltip
            placement="top"
            html={
              isPanelOver()
                ? 'طرح شما به پایان رسیده'
                : isDomainsOver()
                  ? 'تعداد دامنه ها با طرح خریداری شده مطابقت ندارد.'
                  : ''
            }
          >
            <Button
              onClick={() => {
                if (
                  limitInfo?.data?.used?.num_of_domains >=
                  limitInfo?.data?.tariff?.num_of_domains
                )
                  warningMessage(
                    `حداکثر دامنه مجاز طرح شما: ${limitInfo?.data?.tariff?.num_of_domains} `
                  );
                setShowAddDrawer(true);
              }}
              variant="falcon-primary"
              disabled={isFetchingLimitInfo || isPanelOver() || isDomainsOver()}
              size="sm"
              className="d-flex mb-3 py-1 gap-2 align-items-center"
            >
              <FontAwesomeIcon icon="plus" />
              افزودن دامنه
              {(isDomainsOver() || isPanelOver()) && (
                <FontAwesomeIcon icon="lock" />
              )}
            </Button>
          </ChildrensTooltip>
        </Col>
        <Col sm="auto">
          <Form
            onSubmit={e => {
              setSearchTextBody(searchText);
              e.preventDefault();
            }}
          >
            <div className="input-group position-relative input-group-sm">
              <Form.Control
                type="text"
                placeholder="جستجو در لیست دامنه ها"
                name="search_box"
                className="py-1"
                value={searchText}
                onChange={({ target }) => {
                  if (searchTextBody && target.value === '')
                    setSearchTextBody('');
                  setSearchText(target.value);
                }}
              />

              <Button
                variant="falcon-primary"
                type="submit"
                className="border-300 d-flex align-items-center"
              >
                <FontAwesomeIcon icon="search" />
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
      {!isFetching && !isLoadingPagination ? (
        domainsData?.data?.rows.length > 0 ? (
          domainsData?.data?.rows?.map(domain => (
            <DomainCard
              key={domain.slug}
              triggeredRem={triggeredRem}
              setTriggeredRem={setTriggeredRem}
              data={domain}
            />
          ))
        ) : (
          <NoDomainData />
        )
      ) : (
        Array.from({ length: paginationState.perPage }).map((_, index) => (
          <DomainCardSkeleton key={index} />
        ))
      )}
      {!isLoadingPagination ? (
        initDomainsData?.data?.total > 0 ? (
          <PaginationDomains
            currentPage={page}
            total={initDomainsData?.data?.total}
            perPage={paginationState.perPage}
          />
        ) : null
      ) : (
        <PaginationDomainsSkeleton />
      )}
    </>
  );
};

export default Overview;
